import {chain} from 'lodash';

import {ConversationModel} from '../../../src/models/api/conversationModel';
import {ConversationSearchResultModel} from '../../../src/models/api/conversationSearchResultModel';
import {getConversations} from '../../../src/store/data/dataSelectors';
import {
  CellsPanelConversationsPageStateWithSearchResults,
  stateIsCellsPanelPageConversationStateWithSearchResults,
} from '../../../src/store/panels/cells/cellsPanelState';
import {pickMostRecentConversationBase} from '../../../src/store/panels/cells/selectors/cellsPanelSelectionSelectors';
import {getFirstInboxesCellsPanel} from '../../../src/store/panels/panelsSelectors';
import {buildIndexMap, ItemIndexes} from '../../helpers/itemIndexHelpers';
import {AppParametricSelector, createSelector, wrapInShallowSelector} from '../../helpers/reselectHelpers';

export interface ConversationSearchResultModelWithOptionalConversation extends ConversationSearchResultModel {
  readonly conversation?: ConversationModel;
}

export interface CellsConversationSearchResultsContent {
  conversationSearchResults: ReadonlyArray<ConversationSearchResultModelWithOptionalConversation>;
  conversationIndexes: ItemIndexes;
  conversationIds: ReadonlyArray<number>;
}

function maybeAttachConversationToSearchResult(
  conversationSearchResult: ConversationSearchResultModel | undefined,
  conversation: ConversationModel | undefined,
): ConversationSearchResultModelWithOptionalConversation | undefined {
  if (!conversation) {
    return conversationSearchResult;
  }

  if (conversationSearchResult) {
    // If the store's conversation is more recent, update the fields in common with the search result.
    return conversation.updatedAt >= conversationSearchResult.updatedAt
      ? {...conversationSearchResult, conversation}
      : conversationSearchResult;
  }

  return undefined;
}

type GetPage<P> = AppParametricSelector<P, CellsPanelConversationsPageStateWithSearchResults>;
function makeGetPageConversationSearchResults<P>(getPage: GetPage<P>) {
  return createSelector([getPage, getConversations], (page, conversations) =>
    chain(page.conversationIds)
      .map((id) =>
        maybeAttachConversationToSearchResult(page.conversationSearchResults[id], conversations[id]),
      )
      .compact()
      .value(),
  );
}

export function makeGetCellsConversationsSearchResultsContent<P>(getPage: GetPage<P>) {
  const getPageConversationSearchResults = wrapInShallowSelector(
    makeGetPageConversationSearchResults(getPage),
  );
  // We trust the backend's ordering and don't want search results to move around.
  const getSortedConversationIds = wrapInShallowSelector(
    createSelector([getPageConversationSearchResults], (sortedConversationSearchResults) =>
      sortedConversationSearchResults.map((c) => c.id),
    ),
  );
  const getConversationIndexes = createSelector([getSortedConversationIds], (sortedConversationIds) =>
    buildIndexMap(sortedConversationIds),
  );

  return createSelector(
    [getPageConversationSearchResults, getSortedConversationIds, getConversationIndexes],
    (sortedConversationSearchResults, sortedConversationIds, conversationIndexes) => {
      const result: CellsConversationSearchResultsContent = {
        conversationSearchResults: sortedConversationSearchResults,
        conversationIds: sortedConversationIds,
        conversationIndexes,
      };
      return result;
    },
  );
}

// @TODO: Use `makeGetOptionalCellsPanelPage` instead of `getCellsPanel`.
export const getConversationFinder = createSelector(
  [getFirstInboxesCellsPanel, getConversations],
  (cellsPanel, conversations) => {
    const currentPage = cellsPanel?.pages.current;

    if (currentPage && stateIsCellsPanelPageConversationStateWithSearchResults(currentPage)) {
      return (id: number) =>
        pickMostRecentConversationBase(currentPage.conversationSearchResults[id], conversations[id]);
    }

    return (id: number) => conversations[id];
  },
);
